import React, { useState } from 'react'
import { Grid, Box, Typography, Collapse } from '@mui/material'
import {
    MyIdentityLayout,
    SubHeader,
    TableContainer,
    FiltersColumn,
} from 'components'
import TableView from './TableView'
import Card from './Card'
import {
    useIsSmallScreen,
    isNilOrEmpty,
    useGetControlsAccess,
} from 'packages/core'
import { useApplicationSettings, useListingData } from 'hooks'
import { usePageContext } from 'pageContext'
import { Header, Loader } from 'packages/eid-ui'
import { useTranslation } from 'react-i18next'
import { Icon } from 'packages/eid-icons'
import { Card as FilterCard } from 'packages/eid-ui'
import AdvancedFilter from 'components/EidFilter/AdvancedFilter'
import * as DynamicComponents from 'components/DynamicComponents'
import { showSearchToLoadMessage } from 'utils'
import InfoBoxMessage from 'packages/eid-ui/InfoBoxMessage'
import { useAppState } from 'appContext'

const subHeaderHeight = 65

const renderPeopleGrid = (rows, route, fallbackMessage, isSmallScreen) => {
    if (rows.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    height: '40vh',
                    margin: 'auto',
                    alignItems: 'center',
                }}
            >
                <Typography variant="body2">{fallbackMessage}</Typography>
            </Box>
        )
    }

    return (
        <Grid
            container
            spacing={2}
            sx={{
                padding: isSmallScreen ? '0 2rem' : '0 8rem',
            }}
        >
            {rows.map((r, index) => (
                <Grid item xs={12} sm={6} md={4} key={'people-grid-' + index}>
                    <Card data={r} route={route} />
                </Grid>
            ))}
        </Grid>
    )
}

const Listing = ({
    route,
    dataSource,
    attributes,
    filters,
    subHeaderButton,
}) => {
    const { t } = useTranslation()
    const [{searchToLoadConfig}] = useAppState()
    const fallbackMessage = t('NoIdentitiesFound')

    const isSmallScreen = useIsSmallScreen()

    const [showCardView, toggleShowCardView] = useState(false)
    const [openFilters, setOpenFilters] = useState(false)
    const { data: appConfig } = useApplicationSettings()
    const styleConfig = appConfig.style
    const [{ sortBy, sortOrder, searchKey, isEnterPressed }, dispatch] =
        usePageContext()

    const searchIdentity = (text) => {
        dispatch({
            type: 'SEARCH_IDENTITIES',
            payload: text,
        })
    }

    const controls = useGetControlsAccess().map((c) => c.name)

    const searchToLoadConfigValue = searchToLoadConfig?.value ? JSON.parse(searchToLoadConfig?.value) : {}

    const hasAccessToAdvancedSearchFilters =
        filters && controls.includes(filters[0].requireAccess?.control)

    const filteredAttributes = attributes.filter(
        (a) =>
            isNilOrEmpty(a.requireAccess?.control) ||
            controls.includes(a.requireAccess?.control),
    )

    const { list, loading, loadingMore, infiniteScrollObervableDiv } =
        useListingData(
            dataSource.url,
            !showSearchToLoadMessage(route, searchKey, isEnterPressed, searchToLoadConfigValue),
        )

    const renderComponent = (name) => {
        const DynamicComponentToRender = DynamicComponents[name]

        return <DynamicComponentToRender />
    }

    const subHeaderButtonComponent =
        subHeaderButton &&
        (controls.includes(subHeaderButton.requireAccess.control) ? (
            renderComponent(subHeaderButton.name)
        ) : (
            <></>
        ))

    return (
        <MyIdentityLayout
            subHeader={
                <SubHeader
                    showNavTabs={true}
                    showCardView={showCardView}
                    toggleCardView={(showCardView) =>
                        toggleShowCardView(showCardView)
                    }
                    subHeaderButton={subHeaderButtonComponent}
                    showFilters={openFilters}
                    toggleFilters={() => setOpenFilters((prev) => !prev)}
                    showFiltersButton={hasAccessToAdvancedSearchFilters}
                    onTabChange={(tab) => {
                        dispatch({
                            type: 'SET_MULTIPLE_PROPS',
                            payload: { isEnterPressed: false },
                        })
                    }}
                />
            }
        >
            <Box
                sx={{
                    marginTop: '1rem',
                    width: '100%',
                    position: 'relative',
                    paddingX: '1rem',
                    paddingTop: !isSmallScreen
                        ? `${subHeaderHeight + 15}px`
                        : 0,
                }}
                data-ai-explain-section="listing"
            >
                {styleConfig.search?.show && (
                    <div>
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                alignItems: 'center',
                                padding: isSmallScreen
                                    ? '0.8rem 2rem 1.6rem 2rem'
                                    : '0.8rem 0 1.6rem 0',
                                width: isSmallScreen ? '100%' : '33%',
                                float: 'right',
                                flexDirection: 'column',
                                marginTop: '3rem',
                            }}
                            data-ai-explain="Search: type to load identities"
                        >
                            <Header.Search
                                styleData={styleConfig.search?.style}
                                placeholder={t('Search')}
                                handleSearch={searchIdentity}
                                showClearIcon={false}
                                searchInfoMessage={
                                    showSearchToLoadMessage(
                                        route,
                                        searchKey,
                                        isEnterPressed,
                                    )
                                        ? 'PressEnterOrMinSearchCharLimit'
                                        : 'Min_Search_Char_Limit'
                                }
                                onEnterPress={() => {
                                    dispatch({
                                        type: 'SET_MULTIPLE_PROPS',
                                        payload: {
                                            isEnterPressed: true,
                                            searchTerm: '',
                                        },
                                    })
                                }}
                            />
                        </Box>
                        {/* NOTE: Below empty div is intentional just to clear the float which is causing the overlap */}
                        <div style={{ clear: 'both' }}></div>
                    </div>
                )}

                {loading ? (
                    <>
                        <Loader margin="auto" />
                    </>
                ) : list &&
                  !showSearchToLoadMessage(route, searchKey, isEnterPressed) ? (
                    <>
                        {isSmallScreen &&
                            renderPeopleGrid(
                                list,
                                route,
                                fallbackMessage,
                                isSmallScreen,
                            )}

                        {!isSmallScreen && (
                            <Box data-ai-explain="Listing: table or card view">
                                {showCardView ? (
                                    <TableContainer
                                        openFilters={openFilters}
                                        data-ai-explain="Listing grid"
                                    >
                                        {renderPeopleGrid(
                                            list,
                                            route,
                                            fallbackMessage,
                                            isSmallScreen,
                                        )}
                                    </TableContainer>
                                ) : (
                                    <TableContainer
                                        openFilters={openFilters}
                                        data-ai-explain="Listing table"
                                    >
                                        <TableView
                                            fallbackMessage={fallbackMessage}
                                            headings={filteredAttributes}
                                            route={route}
                                            data={list}
                                            sort={(sortBy, sortOrder) => {
                                                dispatch({
                                                    type: 'SORT_IDENTITIES',
                                                    payload: {
                                                        sortBy,
                                                        sortOrder,
                                                    },
                                                })
                                            }}
                                            sortBy={sortBy}
                                            sortOrder={sortOrder}
                                        />
                                    </TableContainer>
                                )}
                            </Box>
                        )}
                    </>
                ) : (
                    <>
                        <Box
                            sx={{
                                margin: 'auto',
                                display: 'table',
                                marginTop: '10rem',
                            }}
                            data-ai-explain="Empty listing: search to load records"
                        >
                            <InfoBoxMessage message={t('SearchToLoad')} />
                        </Box>
                    </>
                )}

                <Box
                    sx={{ position: 'absolute', right: 0 }}
                    data-ai-explain="Advanced search filters"
                >
                    <Collapse in={openFilters} timeout={300} unmountOnExit>
                        <FiltersColumn openFilters={openFilters}>
                            <FilterCard
                                fontColor="black"
                                cardTitle={t('AdvancedSearch')}
                                icon={
                                    <Icon
                                        name="AdvancedSearch"
                                        color="#959598"
                                    />
                                }
                            >
                                <AdvancedFilter
                                    forms={filters && filters[0].forms}
                                    tags={null}
                                />
                            </FilterCard>
                        </FiltersColumn>
                    </Collapse>
                </Box>
            </Box>
            {infiniteScrollObervableDiv}
            {loadingMore &&
                !showSearchToLoadMessage(route, searchKey, isEnterPressed) && (
                    <Loader margin="auto" />
                )}
        </MyIdentityLayout>
    )
}

export default Listing
